import { applyAutoBundleSelection } from "@/components/Pricing/v2/utils";
import { PLAN_REF_ID_MAPPING } from "@/data/plans";
import { TBillingInterval } from "@/types/app";
import { TProductRefId } from "@/types/plan";
import { parseBillingInterval } from "./billingInterval";

export interface PricingPreselection {
  selectedPlanIds: TProductRefId[];
  billingPeriod: TBillingInterval | undefined;
}

// Helper constants

/**
 * Parse plan selection from query parameters
 * Supports multiple formats:
 * - ?select=culling,editing
 * - ?plan=culling,editing
 * - ?product=culling,editing
 * - ?pricing=culling,editing
 * - ?culling=true&editing=true
 */
export function parsePlanSelection(
  searchParams: URLSearchParams,
): TProductRefId[] {
  const selectedPlanIds: TProductRefId[] = [];

  // Method 1: Parse comma-separated values from multiple parameter names
  const parameterNames = ["select", "plan", "product", "pricing"];

  const allPlanRefIds = Object.values(PLAN_REF_ID_MAPPING) as TProductRefId[];

  for (const paramName of parameterNames) {
    const paramValue = searchParams.get(paramName);
    if (paramValue) {
      const planKeys = paramValue.split(",").map((key) => key.trim());

      for (const planKey of planKeys) {
        // Try short key first (culling → AFTERSHOOT_CULLING)
        const mappedId =
          PLAN_REF_ID_MAPPING[
            planKey.toLowerCase() as keyof typeof PLAN_REF_ID_MAPPING
          ];
        if (mappedId && !selectedPlanIds.includes(mappedId)) {
          selectedPlanIds.push(mappedId);
          continue;
        }

        // Try direct product ref ID (AFTERSHOOT_CULLING, AFTERSHOOT_ALL_APP, etc.)
        if (
          allPlanRefIds.includes(planKey as TProductRefId) &&
          !selectedPlanIds.includes(planKey as TProductRefId)
        ) {
          selectedPlanIds.push(planKey as TProductRefId);
        }
      }
    }
  }

  // Method 2: Parse individual plan params (culling=true, editing=true, etc.)
  for (const [queryKey, planId] of Object.entries(PLAN_REF_ID_MAPPING)) {
    const paramValue = searchParams.get(queryKey);
    if (paramValue === "true" || paramValue === "1") {
      if (!selectedPlanIds.includes(planId)) {
        selectedPlanIds.push(planId);
      }
    }
  }

  return selectedPlanIds;
}

/**
 * Parse billing period from query parameters
 * Supports multiple parameter names:
 * - ?interval=yearly
 * - ?billing=yearly
 * - ?period=yearly
 */
export function parseBillingPeriod(
  searchParams: URLSearchParams,
): TBillingInterval | undefined {
  const parameterNames = ["interval", "billing", "period"];

  for (const paramName of parameterNames) {
    const parsed = parseBillingInterval(searchParams.get(paramName));

    if (parsed) return parsed;
  }

  return undefined; // Default
}

/**
 * Parse add-on selection from query parameters
 * Supports: ?addon=<id> or ?addons=<id1>,<id2>
 */
export function parseAddOnSelection(searchParams: URLSearchParams): string[] {
  const paramValue = searchParams.get("addon") || searchParams.get("addons");
  if (!paramValue) return [];
  return paramValue
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

/**
 * Parse promo code from query parameters
 * Supports: ?promo=BLACKFRIDAY24 or ?promo_code=BLACKFRIDAY24 or ?coupon=BLACKFRIDAY24
 */
export function parsePromoCode(searchParams: URLSearchParams): string | null {
  // Try different parameter names for promo codes
  const promoParam =
    searchParams.get("promo") ||
    searchParams.get("promo_code") ||
    searchParams.get("coupon") ||
    searchParams.get("discount");

  if (promoParam && promoParam.trim()) {
    return promoParam.trim().toUpperCase();
  }

  return null;
}

/**
 * Parse all pricing query parameters (composed function)
 * Combines plan selection and billing period parsing
 * @param searchParams - URL search parameters
 */
export function parsePricingQueryParams(
  searchParams: URLSearchParams,
): PricingPreselection {
  const rawPlanIds = parsePlanSelection(searchParams);

  const selectedPlanIds = applyAutoBundleSelection(rawPlanIds);

  const billingPeriod = parseBillingPeriod(searchParams);

  return {
    selectedPlanIds,
    billingPeriod,
  };
}

/**
 * Get plan IDs that should be preselected based on query params
 * Uses the modular approach for better performance when only plans are needed
 * @param searchParams - URL search parameters
 */
export function getPreselectedPlans(searchParams: URLSearchParams): string[] {
  const rawPlanIds = parsePlanSelection(searchParams);
  return applyAutoBundleSelection(rawPlanIds);
}

/**
 * Get billing period that should be preselected based on query params
 * Direct function call for better performance when only billing is needed
 */
export function getPreselectedBilling(
  searchParams: URLSearchParams,
): TBillingInterval | undefined {
  return parseBillingPeriod(searchParams);
}

/**
 * Get promo code that should be preselected based on query params
 * Direct function call for better performance
 */
export function getPreselectedPromoCode(
  searchParams: URLSearchParams,
): string | null {
  return parsePromoCode(searchParams);
}
